import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import {
    hideAppSpinner,
    showAppNotification,
    showAppSpinner,
} from '@rainbow-modules/app';
import Button from 'components/Button';
import Plus from 'components/icons/plus';
import InvoiceList from '../InvoiceList';
import FilterButton from '../InvoiceList/FilterButton';
import CreateInvoiceDrawer from '../CreateInvoiceDrawer';
import EditInvoiceDrawer from '../EditInvoiceDrawer';
import ViewInvoiceDrawer from '../ViewInvoiceDrawer';
import printInvoice from '../printInvoice';
import downloadInvoice from '../downloadInvoice';
import { fetchInvoiceForOutput } from '../api/client';
import { useInvoice } from '../api/hooks';
import { CUSTOMER_INVOICE_FILTER_NAMES } from '../constants';
import { InvoiceRow } from '../types';
import {
    Container,
    Description,
    Header,
    HeaderActions,
    HeaderContent,
    ListWrap,
    Title,
} from './styled';

const CustomerInvoicesTab = () => {
    const { agentId = '', customerId = '' } = useParams();
    const queryClient = useQueryClient();
    const [isCreateOpen, setCreateOpen] = useState(false);
    const [editInvoiceId, setEditInvoiceId] = useState<string | undefined>();
    const [viewInvoiceId, setViewInvoiceId] = useState<string | undefined>();

    const {
        data: invoiceResult,
        isLoading: isLoadingInvoice,
    } = useInvoice(agentId, viewInvoiceId);

    const refreshList = useCallback(() => {
        queryClient.invalidateQueries(['invoice-builder']);
    }, [queryClient]);

    const handlePrint = useCallback(async (row: InvoiceRow) => {
        showAppSpinner();
        try {
            const result = await fetchInvoiceForOutput(agentId, row.id);
            await printInvoice(result.invoice, result.acceptance, agentId);
        } catch (error: any) {
            showAppNotification({
                title: 'Could not print invoice',
                description: error?.message || 'Please try again.',
                icon: 'error',
                timeout: 5000,
            });
        }
        hideAppSpinner();
    }, [agentId]);

    const handleDownload = useCallback(async (row: InvoiceRow) => {
        showAppSpinner();
        try {
            const result = await fetchInvoiceForOutput(agentId, row.id);
            await downloadInvoice(result.invoice, result.acceptance, agentId);
        } catch (error: any) {
            showAppNotification({
                title: 'Could not download invoice',
                description: error?.message || 'Please try again.',
                icon: 'error',
                timeout: 5000,
            });
        }
        hideAppSpinner();
    }, [agentId]);

    return (
        <Container>
            <Header>
                <HeaderContent>
                    <Title>Invoices</Title>
                    <Description>
                        View and create invoices for this customer.
                    </Description>
                </HeaderContent>
                <HeaderActions>
                    <FilterButton
                        label="Filters"
                        organizationId={agentId}
                        filterNames={CUSTOMER_INVOICE_FILTER_NAMES}
                        hideCustomerFilter
                    />
                    <Button
                        variant="outline-brand"
                        size="medium"
                        borderRadius="semi-rounded"
                        onClick={() => setCreateOpen(true)}
                    >
                        <Plus />
                        New
                    </Button>
                </HeaderActions>
            </Header>
            <ListWrap>
                <InvoiceList
                    organizationId={agentId}
                    customerId={customerId}
                    hideCustomerColumn
                    filterNames={CUSTOMER_INVOICE_FILTER_NAMES}
                    onView={(row) => setViewInvoiceId(row.id)}
                    onEdit={(row) => setEditInvoiceId(row.id)}
                    onPrint={handlePrint}
                    onDownload={handleDownload}
                />
            </ListWrap>
            <CreateInvoiceDrawer
                isOpen={isCreateOpen}
                organizationId={agentId}
                lockedCustomerId={customerId}
                onRequestClose={() => setCreateOpen(false)}
                onCreated={refreshList}
            />
            <ViewInvoiceDrawer
                isOpen={Boolean(viewInvoiceId)}
                organizationId={agentId}
                invoice={invoiceResult?.data}
                isLoading={isLoadingInvoice}
                onRequestClose={() => setViewInvoiceId(undefined)}
                onUpdated={refreshList}
                onEdit={(invoice) => setEditInvoiceId(invoice.id)}
            />
            <EditInvoiceDrawer
                isOpen={Boolean(editInvoiceId)}
                organizationId={agentId}
                invoiceId={editInvoiceId}
                lockedCustomerId={customerId}
                onRequestClose={() => setEditInvoiceId(undefined)}
                onUpdated={refreshList}
            />
        </Container>
    );
};

export default CustomerInvoicesTab;
